// Shared helper — fetch the full org member list via SDK and normalise
// the envelope. All call sites that need `sdk.execute('users.members','list')`
// should go through here so envelope unwrap logic lives in one place.
//
// Lives in `shared/functions` (moved from `workspace/functions` 2026-08-09,
// tickets/fable.md "shared/pages/userProfile.js imports a cross-PACKAGE
// sibling") because `shared/pages/userProfile.js` needs it and `shared` is a
// library consumed by workspace/marketplace/canvas — never the other way
// around. `workspace`'s own call sites (loadPrivateData.js, pages/main.js,
// pages/admin/danger.js) now import this file directly via the package
// specifier, the same convention every other cross-package workspace→shared
// reach already uses.
//
// The server's EntityDispatcher discards args for users.members.list so
// the call always returns the full list (capped to ~200 members by the
// server route).
//
// DEDUPE (perf): boot fires this several times (loadPrivateData people fetch +
// its later fan-out, loadAllTeams, grants) — each a ~10s round-trip on a cold
// backend (measured: users/members ×3 on /studio). We coalesce them here:
//   - an in-flight promise is shared by concurrent callers, and
//   - the resolved list is cached for a short TTL so the near-sequential boot
//     callers reuse it too.
// The TTL is short (org roster rarely changes) so post-boot reads stay fresh;
// `invalidateOrgMembers()` clears it explicitly after a membership mutation.

// FA204 / frank trap — `_inflight`/`_inflightOrg`/`_cache`/`_cacheAt`/
// `_cacheOrg` used to live at module scope and get referenced bare from
// both exports below (this function takes an `sdk` arg, not `el`/`root`, so
// there's no DOMQL element to hang state on) — re-homed onto
// `globalThis.__listOrgMembersState` per the mwtFlag.js/pointerCapability.js
// template; each export carries its own self-contained lazy-init of that
// same global object rather than a shared module-scope getter, which would
// be exactly the same bare-sibling-export trap one level down.
// `TTL_MS`/`_unwrap`/`_activeOrg` were pure with one caller
// (listOrgMembers) — inlined alongside it.

// `workspaceId` (optional) — the workspace the TAB is viewing (root.workspace
// .id, URL-derived). When passed, the server scopes the roster to THAT
// workspace's org (membership-verified) instead of the caller's global
// activeOrganization claim, which lags URL-based workspace browsing (multi-
// tab: two tabs on different orgs each get their own roster). The cache/
// in-flight key includes it so tabs on different workspaces never share rows.
export async function listOrgMembers(sdk, workspaceId) {
  if (!globalThis.__listOrgMembersState) {
    globalThis.__listOrgMembersState = {
      inflight: null,
      inflightOrg: null,
      cache: null,
      cacheAt: 0,
      cacheOrg: null
    }
  }
  const state = globalThis.__listOrgMembersState

  const TTL_MS = 15000
  const unwrap = (rows) =>
    Array.isArray(rows) ? rows : rows?.data || rows?.people || rows?.rows || []
  // The org roster is per-organization, so the cache AND the in-flight
  // promise MUST be keyed by the active org id — otherwise a switch within
  // the 15s TTL (or while a fetch under the previous org's token is still
  // in flight) hands the caller the PREVIOUS org's roster (the "people
  // list shows the old org after switching" bug). switchOrg updates
  // sdk._context.activeOrgId before the roster refetch fires, so a
  // mismatched cache / in-flight is treated as a miss.
  const activeOrg = (s) =>
    s?._context?.activeOrgId != null ? String(s._context.activeOrgId) : null

  const org = (workspaceId ? 'ws:' + workspaceId + ':' : '') + activeOrg(sdk)
  if (state.cache && state.cacheOrg === org && Date.now() - state.cacheAt < TTL_MS)
    return state.cache
  if (state.inflight && state.inflightOrg === org) return state.inflight
  const inflight = (async () => {
    const rows = await sdk
      .execute('users.members', 'list', workspaceId ? { workspaceId } : undefined)
      .catch(() => null)
    const list = unwrap(rows)
    // Don't cache an empty/failed result — let the next caller retry.
    if (list.length) {
      state.cache = list
      state.cacheAt = Date.now()
      state.cacheOrg = org
    }
    return list
  })()
  state.inflight = inflight
  state.inflightOrg = org
  try {
    return await inflight
  } finally {
    // Only clear if a concurrent different-org call hasn't already replaced it.
    if (state.inflight === inflight) {
      state.inflight = null
      state.inflightOrg = null
    }
  }
}

// Clear the cache after a membership change (invite/remove) so the next
// listOrgMembers() re-fetches instead of serving the stale roster.
export function invalidateOrgMembers() {
  if (!globalThis.__listOrgMembersState) return
  globalThis.__listOrgMembersState.cache = null
  globalThis.__listOrgMembersState.cacheAt = 0
  globalThis.__listOrgMembersState.cacheOrg = null
}
